import {Component, OnInit, ViewChild} from '@angular/core';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {Select, Store} from '@ngxs/store';
import {StorageService} from '../../services/storage/storage.service';
import {Loader} from '../../models/loader/loader.redux';
import {IUser} from '../../models/auth';
import {ServiceAction, ServiceState} from '../../models/service';
import {IMeasurementTemplate, MeasurementAction, MeasurementState} from '../../models/measurement';
import {ActivatedRoute, Params} from '@angular/router';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {IMeasurementRow, MeasurementRowEnum, NewMeasurementComponent} from '../new-measurement/new-measurement.component';
import {Logger} from '../../class/helper/logger.class';
import {cloneDeep, uniqWith} from 'lodash';
import {firstValueFrom, Observable} from 'rxjs';
import {DateHelper} from '../../class/helper/date.helper.class';
import {PatientMeasurementRequestDTO} from '../../dtos/measurement';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';

@Component({
  selector: 'app-consultation-measurement',
  templateUrl: './consultation-measurement.component.html',
  styleUrls: ['./consultation-measurement.component.scss'],
  animations: [fadeInAnimation]
})
export class ConsultationMeasurementComponent implements OnInit {
  @Select(MeasurementState.getMeasurementsTemp) measurementsTemp$: Observable<IMeasurementRow[]>;
  @ViewChild(NewMeasurementComponent) newMeasurementComponent: NewMeasurementComponent;

  measurements: any[] = [];
  loadMeasurements: boolean;
  expertId: string;
  clinicId: string;
  patientId: string;
  serviceId: string;
  appointmentId: string;
  measurementsRowFromTemplate: IMeasurementRow[] = [];
  templates: IMeasurementTemplate[] = [];

  templatesSelectedMap = new Map<number, IMeasurementTemplate>();

  constructor(
    private store: Store,
    private storageService: StorageService,
    private utilitiesService: UtilitiesService,
    private activatedRoute: ActivatedRoute,
    private messageAlertService: MessageAlertService,
  ) { }

  ngOnInit(): void {
    this.initValuesHttp().then();
    this.setRouterParams().then();
  }

  get hasUnsavedMeasurements(): boolean {
    const measurementsTemp: IMeasurementRow[] = this.store.selectSnapshot(MeasurementState.getMeasurementsTemp);
    return measurementsTemp.filter(({saved}: IMeasurementRow) => !saved).length > 0;
  }

  /**
   * Lanza las primeras peticiones al servidor que sean necesarias.
   */
  async initValuesHttp(): Promise<void> {
    await firstValueFrom((this.store.dispatch(new Loader(true, 'Obteniendo la información ...'))));
    const userLogged = this.storageService.getLocalItem<{ user: IUser, token: string }>('user')?.user;
    if (userLogged && userLogged.experts) {
      this.expertId = userLogged.experts[0]._id;
    }
    this.serviceId = await firstValueFrom((this.store.selectOnce(ServiceState.getServiceSelectedId)));
    if (!this.serviceId) {
      await firstValueFrom((this.store.dispatch(new ServiceAction.GetActive(this.patientId, this.expertId))));
      this.serviceId = await firstValueFrom((this.store.selectOnce(ServiceState.getServiceSelectedId)));
    }
    await firstValueFrom((this.store.dispatch(new MeasurementAction.GetTemplatesMeasurements(this.expertId))));
    this.templates = cloneDeep(await firstValueFrom((this.store.selectOnce(MeasurementState.getMeasurementsTemplate))));

    if (this.templates.length > 0) {
      // this.onChangeMeasurementTemplate(this.templates[0], 0);
      this.templatesSelectedMap.set(0, this.templates[0]);
    }
    const index = this.templates.findIndex(item => item.default);
    if (index !== -1) {
      this.templatesSelectedMap.set(index, this.templates[index]);
      this.measurementsRowFromTemplate = this.templatesSelectedMap.get(index).measurementsRow;
    }
    // el error ocurre en clinicas viejas que no tengan un template por defecto
    await this.getDataHistorialOfMeasurements();
    this.loadMeasurements = true;
    await firstValueFrom((this.store.dispatch(new Loader(false))));
  }

  /**
   * Se agrega una fila nueva vacia para poder agregar una nueva medición.
   */
  addEmptyNewToTable(): void {
    try {
      if (!this.measurementsRowFromTemplate) return;
      const empty = this.measurementsRowFromTemplate.find(item => item.status === MeasurementRowEnum.NEW);
      if (!empty) {
        this.measurementsRowFromTemplate.push(this.newMeasurementComponent.returnMeasurementRow());
      }
    } catch (e) {
      Logger.log('Ocurrio un error', e);
    }
  }

  /**
   * Obtiene el historial de mediciones del paciente.
   */
  async getDataHistorialOfMeasurements(): Promise<void> {
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Obteniendo información de mediciones ...'))));
      await firstValueFrom(this.store.dispatch(new MeasurementAction.GetPatientMeasurementHeaders(
        this.patientId,
        this.serviceId)
      ));
      await this.createMeasurementsRows();
    } catch (e) {
      Logger.log('Ocurrio un error', e);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  /**
   * Cambia de template de medida.
   * @param template plantilla de la medida
   * @param index índice de la plantilla
   */
  async onChangeMeasurementTemplate(template: IMeasurementTemplate, index: number): Promise<void> {
    await firstValueFrom((this.store.dispatch(new Loader(true))));
    if (this.templatesSelectedMap.has(index)) {
      this.templatesSelectedMap.delete(index);
    } else {
      this.templatesSelectedMap.set(index, template);
    }
    this.measurementsRowFromTemplate = [];
    if (this.templatesSelectedMap.size > 0) {
      for (const value of this.templatesSelectedMap.values()) {
        this.measurementsRowFromTemplate = [...this.measurementsRowFromTemplate, ...value.measurementsRow];
      }
    }

    await this.createMeasurementsRows();
    await firstValueFrom((this.store.dispatch(new Loader(false))));
  }

  /**
   * Crear todas las filas de la tabla con los datos de las mediciones.
   */
  async createMeasurementsRows(): Promise<void> {
    // obtiene los headers, por cada header se crea una fila en la tabla, cuando esas filas estén correctas se guardan en temporal
    const headers = cloneDeep(await firstValueFrom((this.store.selectOnce(MeasurementState.getMeasurementsHistorialHeaders))));

    // se iteran los headers, antes de agregar un nuevo row se verifica si no existe ya en los del template
    // si no existe entonces se agrega
    for (const item of headers) {
      if (!item) continue;
      const index = this.measurementsRowFromTemplate.findIndex(measure => measure._id === item._id);

      if (index !== -1) {
        this.measurementsRowFromTemplate[index].lastValue = item.lastValue;
        this.measurementsRowFromTemplate[index].unitMeasurementDefault = item.defaultMeasurement;
        this.measurementsRowFromTemplate[index].showEdit = true;
        this.measurementsRowFromTemplate[index].unit = item.defaultMeasurement._id;
        this.measurementsRowFromTemplate[index].name = item.name;
        this.measurementsRowFromTemplate[index].name_es = item.name_es;
      } else {
        const value = {
          _id: item._id,
          name: item.name,
          name_es: item.name_es,
          unitMeasurement: [item.defaultMeasurement],
          measurementSelected: null,
          unit: item.defaultMeasurement._id,
          unitMeasurementDefault: item.defaultMeasurement,
          value: null,
          status: MeasurementRowEnum.FILL,
          lastValue: item.lastValue,
          showEdit: true
        };
        value.measurementSelected = value;
        this.measurementsRowFromTemplate.push(value);
      }
    }
    this.measurementsRowFromTemplate = cloneDeep(this.merge(this.measurementsRowFromTemplate));
    this.addEmptyNewToTable();
  }

  // tslint:disable:typedef
  merge(templates) {
    return uniqWith(templates, this.compareAndMerge);
  }

  compareAndMerge(first, second) {
    return first._id === second._id;
  }

  /**
   * Setea los parámetros de la url.
   */
  async setRouterParams(): Promise<void> {
    const params: Params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.clinicId = params.clinicId;
    this.patientId = params.patientId;
    this.activatedRoute.queryParams.subscribe(value => {
      this.appointmentId = value.appointmentId;
      if (value.expert) {
        this.expertId = value.expert;
      }
    });
  }

  /**
   * Guarda en redux la fila con los datos de medida solo si los campos son válidos.
   * @param measurement medida IMeasurementRow
   */
  async onAddValidRow(measurement: IMeasurementRow): Promise<void> {
    await firstValueFrom((this.store.dispatch(new MeasurementAction.AddMeasurementTemporal(measurement))));
  }

  /**
   * Remueve una fila de la tabla.
   * @param id id de la medida
   */
  async onRemoveRow(id: string): Promise<void> {
    await firstValueFrom((this.store.dispatch(new MeasurementAction.RemoveMeasurementTemporal(id))));
  }

  async saveBasicFormulas(): Promise<void> {
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Guardando mediciones ...'))));
      const dateHelper = new DateHelper();
      const measurementsTemporal: IMeasurementRow[] = this.store.selectSnapshot(MeasurementState.getMeasurementsTemp);
      const data: PatientMeasurementRequestDTO = {
        dateMetadata: dateHelper.convertISOStringToDateMetadata(new Date()),
        measurements: this.utilitiesService.convertMeasurementsRowToMeasurements(measurementsTemporal),
        serviceId: this.serviceId,
      };
      await firstValueFrom(this.store.dispatch(new MeasurementAction.Post(this.clinicId, this.patientId, this.expertId, data)));
      await firstValueFrom(this.store.dispatch(new MeasurementAction.ChangeStatusToSavedMeasurementTemporal()));
      this.messageAlertService.createMessage('success', 'Medidas registradas con éxito.');
    } catch (e) {
      console.error('[saveBasicFormulas] ', e?.message);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }
}
